import {
  intranetAiAgentMineSchema,
  intranetAiAskDeleteAllResponseSchema,
  intranetAiAskHistorySchema,
  INTRANET_AI_ASK_PAGE_SIZE,
  intranetAiAskResponseSchema,
  intranetAiAssistantSchema,
  intranetAiDraftResponseSchema,
  type IntranetAiAskRequest,
  type IntranetAiDraftRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';

export const intranetAssistantQueryKey = ['intranet', 'ai', 'assistant'] as const;
export function intranetAiAskHistoryQueryKey(
  scope: 'intranet' | 'agent',
  page: number,
  pageSize: number,
) {
  return ['intranet', 'ai', 'asks', { scope, page, pageSize }] as const;
}

const okSchema = z.object({ ok: z.literal(true) });

export function useIntranetAssistant() {
  return useQuery({
    queryKey: intranetAssistantQueryKey,
    queryFn: () => apiFetch('/intranet/ai/assistant', intranetAiAssistantSchema),
    refetchOnWindowFocus: true,
  });
}

export function useDraftIntranetAi() {
  return useMutation({
    mutationFn: (body: IntranetAiDraftRequest) =>
      apiFetch('/intranet/ai/draft', intranetAiDraftResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useAskIntranetAi() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: IntranetAiAskRequest) =>
      apiFetch('/intranet/ai/ask', intranetAiAskResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['intranet', 'ai', 'asks'] });
    },
  });
}

export function useIntranetAiAskHistory(
  enabled: boolean,
  scope: 'intranet' | 'agent',
  page: number,
  pageSize = INTRANET_AI_ASK_PAGE_SIZE,
) {
  return useQuery({
    queryKey: intranetAiAskHistoryQueryKey(scope, page, pageSize),
    queryFn: () =>
      apiFetch(
        `/intranet/ai/asks?page=${page}&pageSize=${pageSize}&scope=${scope}`,
        intranetAiAskHistorySchema,
      ),
    enabled,
  });
}

export function useDeleteIntranetAiAsk() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/ai/asks/${encodeURIComponent(id)}`, okSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['intranet', 'ai', 'asks'] });
    },
  });
}

export function useDeleteAllIntranetAiAsks() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (scope: 'intranet' | 'agent') =>
      apiFetch(`/intranet/ai/asks?scope=${scope}`, intranetAiAskDeleteAllResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['intranet', 'ai', 'asks'] });
    },
  });
}

export const intranetAiAgentsMineQueryKey = ['intranet', 'ai', 'agents', 'mine'] as const;

export function useIntranetAiAgentsMine(enabled: boolean) {
  return useQuery({
    queryKey: intranetAiAgentsMineQueryKey,
    queryFn: () => apiFetch('/intranet/ai/agents/mine', intranetAiAgentMineSchema),
    enabled,
  });
}
